import React, { useState, useEffect, useRef } from "react";
import { Network, ChevronDown, Check, Search, Plus, Trash2, Cpu } from "lucide-react";
import { toast } from "sonner";
import { useWorkflow } from "../../../../context/WorkflowContext";
import { useHat } from "../../../../context/HatContext";
import { useModel } from "../../../../context/ModelContext";
import { Workflow } from "../../../../types";
import WorkflowDiagram from "./WorkflowDiagram";

interface CreateWorkflowFormProps {
  workflowData?: Workflow;
  onSave: () => void;
  onCancel: () => void;
}

const MIN_CHAIN_LENGTH = 2;
const MAX_CHAIN_LENGTH = 5;

const slotLabel = (index: number) => `Assistant ${index + 1}`;
const slotHint = (index: number, total: number) => {
  if (index === 0) return "Receives the user's question first.";
  if (index === total - 1) return "Produces the final response shown to the user.";
  return `Reads Assistant ${index}'s output and refines it.`;
};

// Custom Dropdown Component
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ id?: string; hat_name: string }>;
  placeholder: string;
  disabled?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredIndex(-1);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find((option) => option.id === value);

  const filteredOptions = options.filter(
    (opt) => opt.id && opt.hat_name && opt.hat_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setHoveredIndex(-1);
    setSearchQuery("");
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (hoveredIndex >= 0 && filteredOptions[hoveredIndex]?.id) {
          handleSelect(filteredOptions[hoveredIndex].id!);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHoveredIndex(-1);
        setSearchQuery("");
        break;
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHoveredIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          setHoveredIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        }
        break;
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        event.stopPropagation();
        setSearchQuery("");
        setIsOpen(false);
        setHoveredIndex(-1);
        break;
      case "ArrowDown":
        event.preventDefault();
        setHoveredIndex(0);
        break;
      case "Enter":
        event.preventDefault();
        if (filteredOptions.length > 0 && hoveredIndex >= 0 && filteredOptions[hoveredIndex]?.id) {
          handleSelect(filteredOptions[hoveredIndex].id!);
        } else if (filteredOptions.length === 1 && filteredOptions[0].id) {
          handleSelect(filteredOptions[0].id);
        }
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleDropdownKeyDown}
        disabled={disabled}
        className={`
          w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm
          border border-gray-300 rounded-lg bg-white text-left
          focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none
          transition-colors flex items-center justify-between
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}
        `}
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
          {selectedOption ? selectedOption.hat_name : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHoveredIndex(-1);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search assistants..."
                className="w-full pl-7 pr-2 py-1.5 bg-white text-gray-900 text-xs sm:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="overflow-auto max-h-52">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs sm:text-sm text-gray-500">
                {searchQuery ? "No assistants found" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.id!)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  className={`
                  w-full px-3 py-2 text-xs sm:text-sm text-left transition-colors
                  flex items-center justify-between
                  ${hoveredIndex === index ? "bg-purple-600 text-white" : "text-gray-900 hover:bg-purple-600 hover:text-white"}
                  ${value === option.id ? "bg-purple-100 text-purple-900" : ""}
                  ${hoveredIndex === index && value === option.id ? "bg-purple-600 text-white" : ""}
                `}
                >
                  <span>{option.hat_name}</span>
                  {value === option.id && (
                    <Check className={`w-4 h-4 ${hoveredIndex === index ? "text-white" : "text-purple-600"}`} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CreateWorkflowForm: React.FC<CreateWorkflowFormProps> = ({
  workflowData,
  onSave,
  onCancel,
}) => {
  const { createWorkflow, updateWorkflowData, validateWorkflow } = useWorkflow();
  const { hatsList, fetchHatsList } = useHat();
  const { models } = useModel();

  const [workflowName, setWorkflowName] = useState(workflowData?.workflow_name || "");
  const [description, setDescription] = useState(workflowData?.description || "");
  const [assistantChain, setAssistantChain] = useState<string[]>(() => {
    const initial = workflowData?.assistant_chain ?? [];
    if (initial.length >= MIN_CHAIN_LENGTH && initial.length <= MAX_CHAIN_LENGTH) {
      return [...initial];
    }
    // Default: start with MIN_CHAIN_LENGTH empty slots
    return Array(MIN_CHAIN_LENGTH).fill("");
  });
  const [modelId, setModelId] = useState<string>(workflowData?.model_id || "");
  const [isPublic, setIsPublic] = useState(workflowData?.is_public || false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const isEditMode = !!workflowData?.id;

  useEffect(() => {
    fetchHatsList();
  }, [fetchHatsList]);

  const availableHats = hatsList.filter((hat) => hat.id);

  const handleSlotChange = (index: number, assistantId: string) => {
    setAssistantChain((prev) => {
      const next = [...prev];
      next[index] = assistantId;
      return next;
    });
  };

  const handleAddSlot = () => {
    if (assistantChain.length >= MAX_CHAIN_LENGTH) return;
    setAssistantChain((prev) => [...prev, ""]);
  };

  const handleRemoveSlot = (index: number) => {
    if (assistantChain.length <= MIN_CHAIN_LENGTH) return;
    setAssistantChain((prev) => prev.filter((_, i) => i !== index));
  };

  const optionsForSlot = (index: number) => {
    const otherSelections = assistantChain.filter((id, i) => i !== index && id);
    return availableHats.filter(
      (hat) => hat.id && !otherSelections.includes(hat.id)
    );
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!workflowName.trim()) errors.push("Workflow name is required");
    if (!description.trim()) errors.push("Description is required");

    if (
      assistantChain.length < MIN_CHAIN_LENGTH ||
      assistantChain.length > MAX_CHAIN_LENGTH
    ) {
      errors.push(
        `Chain must contain between ${MIN_CHAIN_LENGTH} and ${MAX_CHAIN_LENGTH} assistants`
      );
    }

    assistantChain.forEach((id, i) => {
      if (!id) errors.push(`${slotLabel(i)} is required`);
    });

    const filled = assistantChain.filter((id) => id);
    if (filled.length === assistantChain.length && new Set(filled).size !== filled.length) {
      errors.push("Each assistant in the chain must be different");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setIsSaving(true);
    setValidationErrors([]);

    try {
      const validation = await validateWorkflow(assistantChain);

      if (!validation.valid) {
        setValidationErrors(validation.errors || ["Workflow validation failed"]);
        toast.error("Workflow validation failed");
        setIsSaving(false);
        return;
      }

      const workflowPayload: Workflow = {
        workflow_name: workflowName,
        description,
        assistant_chain: assistantChain,
        model_id: modelId || undefined,
        is_public: isPublic,
      };

      if (isEditMode && workflowData?.id) {
        await updateWorkflowData(workflowData.id, workflowPayload);
      } else {
        await createWorkflow(workflowPayload);
      }

      onSave();
    } catch (error: any) {
      console.error("Error saving workflow:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to save workflow";
      if (error.response?.data?.details) {
        setValidationErrors(error.response.data.details);
      }
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const getAssistantName = (assistantId: string) => {
    const assistant = availableHats.find((hat) => hat.id === assistantId);
    return assistant?.hat_name || "Unknown Assistant";
  };

  const allSlotsFilled =
    assistantChain.length >= MIN_CHAIN_LENGTH &&
    assistantChain.length <= MAX_CHAIN_LENGTH &&
    assistantChain.every((id) => id);

  const canAddMore = assistantChain.length < MAX_CHAIN_LENGTH;
  const canRemove = assistantChain.length > MIN_CHAIN_LENGTH;

  return (
    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
        <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">
          {isEditMode ? "Edit Workflow" : "Add New Workflow"}
        </h3>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mb-4 p-2 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm sm:text-base">
            <h4 className="text-xs sm:text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h4>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-xs sm:text-sm text-red-700">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Workflow Name */}
        <div className="mb-3 sm:mb-4">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Workflow Name *
          </label>
          <input
            type="text"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            className="w-full px-2 py-1.5 sm:px-3 sm:py-2 bg-white text-gray-900 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            placeholder="e.g., Generator → Critic → Finalizer"
            required
          />
        </div>

        {/* Description */}
        <div className="mb-3 sm:mb-4">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Description *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 sm:px-3 sm:py-2 bg-white text-gray-900 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            placeholder="Describe what this workflow does..."
            required
          />
        </div>

        {/* Assign Model — workflow-level model override */}
        <div className="mb-3 sm:mb-4">
          <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            <Cpu size={14} className="sm:w-4 sm:h-4 text-violet-600" />
            Assign Model
          </label>
          <p className="text-xs sm:text-sm text-gray-500 mb-2">
            Optional. If selected, this model is used by all assistants in this workflow,
            overriding any model configured on individual assistants.
          </p>
          <CustomDropdown
            value={modelId}
            onChange={(value) => setModelId(value)}
            options={models
              .filter((m) => m.is_enabled)
              .map((m) => ({
                id: m.foundation_model_id,
                hat_name: m.display_name || m.name,
              }))}
            placeholder="Select a model (optional)..."
          />
        </div>

        {/* Chain slots */}
        <div className="mb-3 sm:mb-4">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Assistant Chain *
          </label>
          <p className="text-xs sm:text-sm text-gray-500 mb-3">
            Pick {MIN_CHAIN_LENGTH} to {MAX_CHAIN_LENGTH} assistants. The user's question flows through them in order — only the last assistant's output is shown to the user.
          </p>

          <div className="space-y-3">
            {assistantChain.map((slotValue, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-2 sm:p-3 bg-gray-50"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-medium text-gray-900">
                      {slotLabel(index)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {slotHint(index, assistantChain.length)}
                    </div>
                  </div>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => handleRemoveSlot(index)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      title={`Remove ${slotLabel(index)}`}
                    >
                      <Trash2 size={14} className="sm:w-4 sm:h-4" />
                    </button>
                  )}
                </div>
                <CustomDropdown
                  value={slotValue}
                  onChange={(value) => handleSlotChange(index, value)}
                  options={optionsForSlot(index)}
                  placeholder={`Select ${slotLabel(index)}...`}
                />
              </div>
            ))}
          </div>

          {/* Add slot button */}
          {canAddMore ? (
            <button
              type="button"
              onClick={handleAddSlot}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm border border-dashed border-violet-300 text-violet-600 hover:bg-violet-50 hover:border-violet-400 rounded-lg transition-colors"
            >
              <Plus size={14} className="sm:w-4 sm:h-4" />
              Add Assistant
            </button>
          ) : (
            <p className="mt-3 text-xs sm:text-sm text-amber-600 italic text-center">
              Maximum of {MAX_CHAIN_LENGTH} assistants reached
            </p>
          )}
        </div>

        {/* Public/Private */}
        <div className="mb-3 sm:mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-xs sm:text-sm font-medium text-gray-700">
              Make this workflow public
            </span>
          </label>
          <p className="ml-6 text-xs sm:text-sm text-gray-500">
            Public workflows can be used by all users
          </p>
        </div>

        {/* Workflow Preview */}
        {allSlotsFilled && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-3 sm:mb-4 flex items-center gap-2">
              <Network size={14} className="sm:w-4 sm:h-4" />
              Chain Preview
            </h4>
            <WorkflowDiagram
              chain={assistantChain.map((id) => ({
                id,
                name: getAssistantName(id),
              }))}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">{isEditMode ? "Updating..." : "Creating..."}</span>
                <span className="sm:hidden">{isEditMode ? "Updating" : "Creating"}</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">{isEditMode ? "Update Workflow" : "Create Workflow"}</span>
                <span className="sm:hidden">{isEditMode ? "Update" : "Create"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateWorkflowForm;
